// The one frame loop behind the sky. Each frame it eases the drift toward
// its target, adds the board's pan for the parallax, moves the depth layers
// (transforms only, so the compositor does the work), slides a layer's
// window of tiles when a tile scrolls out, and runs the animated bits.
// Nothing here renders React; the stage hands it the elements.
import type { Weekday } from '../../systems/api/types';
import { playSession } from '../../systems/play/playSession';
import { AmbientRuntime, type AmbientKind } from './ambient/ambientRuntime';
import { ambientSettings } from './ambient/ambientSettings';
import type { View } from './ambient/schedule';
import { driftTarget, driftVelocity, firstTile, stepOffsets } from './drift';
import { isReducedMotion } from './reducedMotion';
import { boardMotion } from './skyMotionStore';
import { HALF_HEIGHT, TILE_WIDTH, type BackgroundStar, type DepthLayer } from './weekdaySkies';

export type Half = 'home' | 'play';

export interface MotionInputs {
  weekday: Weekday;
  /** The half in view, where the animated bits appear. */
  half: Half;
  /** Home at rest or arriving: the sky drifts. */
  drifting: boolean;
  /** No transition under way: the animated bits may start. */
  resting: boolean;
}

interface LayerHandle {
  el: HTMLElement;
  depth: DepthLayer;
  onFirstTile: (k: number) => void;
  first: number;
  written: number;
}

/** The running sky, for the tuning panel's Fire buttons. */
export const activeSky: { current: SkyMotion | null } = { current: null };

export class SkyMotion {
  inputs: MotionInputs = { weekday: 'thursday', half: 'home', drifting: false, resting: false };
  view: View = { width: 0, height: 0, scale: 1 };
  stars: readonly BackgroundStar[] = [];
  private offsets: number[] = [0, 0, 0];
  private velocity = 0;
  private layers = new Set<LayerHandle>();
  private ambient: AmbientRuntime | null = null;
  private board: { session: number; pan: number } | null = null;
  private raf = 0;
  private last = 0;

  start(): void {
    if (this.raf) return;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  stop(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.ambient?.clear();
  }

  addLayer(el: HTMLElement, depth: DepthLayer, onFirstTile: (k: number) => void): () => void {
    const handle: LayerHandle = { el, depth, onFirstTile, first: Number.NaN, written: Number.NaN };
    this.layers.add(handle);
    this.place(handle);
    return () => this.layers.delete(handle);
  }

  attachAmbient(layer: HTMLElement, starRoot: HTMLElement): () => void {
    this.ambient = new AmbientRuntime(layer, starRoot);
    return () => {
      this.ambient?.clear();
      this.ambient = null;
    };
  }

  fire(kind: AmbientKind): void {
    this.ambient?.fire(kind);
  }

  reschedule(): void {
    this.ambient?.reschedule();
  }

  /** After a resize: the pixel positions all change. */
  relayout(): void {
    for (const h of this.layers) {
      h.written = Number.NaN;
      this.place(h);
    }
  }

  private frame = (now: number): void => {
    this.raf = requestAnimationFrame(this.frame);
    // A hidden tab gets no frames; the gap on return is not counted.
    const dt = Math.min(64, Math.max(0, now - this.last));
    this.last = now;
    const cfg = ambientSettings.get();
    const reduced = isReducedMotion();

    const target = this.inputs.drifting && !reduced ? driftTarget(cfg.drift.speed, cfg.drift.direction) : 0;
    this.velocity = reduced ? 0 : driftVelocity(this.velocity, target, dt, cfg.drift.settleMs, cfg.drift.easeInMs);

    const b = boardMotion.get();
    const panDelta = !reduced && b && this.board && b.session === this.board.session ? b.pan - this.board.pan : 0;
    this.board = b ? { session: b.session, pan: b.pan } : null;

    if (this.velocity !== 0 || panDelta !== 0) {
      this.offsets = stepOffsets(this.offsets, this.velocity, dt, cfg.drift.layerFactors, panDelta, cfg.parallax.panFactors);
      for (const h of this.layers) this.place(h);
    }

    const play = this.inputs.half === 'play';
    this.ambient?.tick(dt, {
      cfg,
      day: cfg.days[this.inputs.weekday],
      gate: {
        resting: this.inputs.resting,
        reduced,
        wordInProgress: play && (playSession.get().board?.selection.length ?? 0) > 0,
      },
      view: this.view,
      halfTop: play ? 0 : HALF_HEIGHT,
      offsets: this.offsets,
      stars: this.stars,
      zones: play ? (b?.zones ?? []) : [],
    });
  };

  private place(h: LayerHandle): void {
    const offset = this.offsets[h.depth] ?? 0;
    const px = -offset * this.view.scale;
    if (px !== h.written) {
      h.el.style.transform = `translate3d(${px}px, 0, 0)`;
      h.written = px;
    }
    const first = firstTile(offset, TILE_WIDTH);
    if (first !== h.first) {
      h.first = first;
      h.onFirstTile(first);
    }
  }
}
